/**
 * PinLockContext.jsx
 *
 * Soft session lock for shared-hardware roles (ClinicManager primarily).
 *
 * Threat model: a different person sitting down at an unattended terminal.
 * NOT extracting tokens via DevTools — MSAL state stays in memory, the API
 * still trusts the bearer token. PIN gates the UI overlay only.
 *
 * Lock triggers:
 *   - 15 minutes of inactivity (configurable in pinLockLogic.js)
 *   - Tab hidden > 30s
 *   - Manual "Lock now" from the user menu
 *   - Any other tab in the same browser locks (BroadcastChannel)
 *
 * Unlock:
 *   - Server-verified PIN (lib/pin.js) with throttling
 *
 * First-time setup is forced for required roles before they can use the app.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../auth/AuthContext.jsx';
import { getPinStatus, setPin, verifyPin, recordAccessSession, setLockPref } from '../api/client.js';
import { setBootStage } from '../lib/bootStage.js';
import {
  INACTIVITY_MS,
  HIDDEN_GRACE_MS,
  BROADCAST_CHANNEL,
  SESSION_KEY_LAST_ACTIVE,
  SHARED_KEY_LAST_ACTIVE,
  isInactive,
  shouldLockOnVisibilityRestore,
  applyBroadcastTransition,
  resolveLock,
} from '../lib/pinLockLogic.js';

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'wheel'];
const SESSION_LOG_KEY = 'ae-access-session-logged';

// Feature flag for the PIN session lock. Re-enabled 2026-06-24 after the
// multi-tab cross-tab re-lock fix (see PINLOCK_ANALYSIS.md / pinLockLogic.js).
// Flip to `false` to kill the overlay app-wide without any other change.
const PIN_LOCK_ENABLED = true;

// ── Activity stamps ──────────────────────────────────────────────────────────
// Per-tab (sessionStorage) + a SHARED cross-tab stamp (localStorage) so a user
// active in ANY tab keeps every tab alive. The shared write is throttled to ~1s
// to avoid hammering localStorage on every mousemove; `force` bypasses it for
// unlock transitions where freshness must be immediate.
let _lastSharedWrite = 0;
function markActive(force = false) {
  const now = Date.now();
  try { sessionStorage.setItem(SESSION_KEY_LAST_ACTIVE, String(now)); } catch {}
  if (force || now - _lastSharedWrite > 1000) {
    _lastSharedWrite = now;
    try { localStorage.setItem(SHARED_KEY_LAST_ACTIVE, String(now)); } catch {}
  }
}
function readPerTab() { try { return parseInt(sessionStorage.getItem(SESSION_KEY_LAST_ACTIVE) || '0', 10); } catch { return 0; } }
function readShared() { try { return parseInt(localStorage.getItem(SHARED_KEY_LAST_ACTIVE) || '0', 10); } catch { return 0; } }

const PinLockContext = createContext(null);

export function usePinLock() {
  return useContext(PinLockContext);
}

export function PinLockProvider({ children }) {
  // The session lock is tied to the physical person, so it always follows the
  // real signed-in user — never an impersonated ("assume identity") role. The
  // backend PIN endpoints mirror this via realIdentity().
  const { realUser } = useAuth();
  const email = realUser?.email || null;
  const role  = realUser?.role  || null;
  const isSuperAdmin = !!realUser?.isSuperAdmin;
  // Everyone with a role must set/use a PIN — unless the feature is killed.
  // ClinicMailbox is exempt: the shared login has no personal Entra PIN, and its
  // sub-identity PIN (SubIdentityGate) IS the lock. Requiring one here would
  // force a *shared* PIN on the whole clinic — the worst regression.
  const required = PIN_LOCK_ENABLED && !!role && role !== 'ClinicMailbox';
  // Per-role session-lock override (from the role profile, projected onto the
  // user in roles.js). Absent fields inherit the code defaults.
  const sessionLock  = realUser?.sessionLock || null;
  const roleExempt   = !!sessionLock?.exempt;
  const roleInactivityMs  = sessionLock?.inactivityMs || INACTIVITY_MS;
  const roleHiddenGraceMs = sessionLock?.hiddenGraceMs || HIDDEN_GRACE_MS;

  // Per-USER auto-lock duration preference (user setting; Supervisor + SuperAdmin).
  // Fetched from /pin/status alongside the PIN state. undefined = not loaded yet,
  // null = no preference (role default), 0 = "Never", >0 = chosen window (ms).
  const [lockPrefMs, setLockPrefMs] = useState(undefined);
  const prefMs = typeof lockPrefMs === 'number' ? lockPrefMs : null;

  // Effective behaviour = role defaults resolved against the user's preference.
  // A SuperAdmin (code) normally locks manually only; a positive preference lets
  // them opt IN to auto-lock, and "Never" (0) keeps the manual-only behaviour.
  // A role flagged `exempt` and any non-required role never auto-lock.
  const { autoLock, inactivityMs, hiddenGraceMs } = resolveLock({
    required, isSuperAdmin, roleExempt, roleInactivityMs, roleHiddenGraceMs, prefMs,
  });

  // Only Supervisor and SuperAdmin may configure their own duration for now.
  const canSetLockDuration = required && (isSuperAdmin || role === 'Supervisor');

  // Lifecycle stages:
  //  loading  — fetching /pin/status
  //  setup    — user must create a PIN (required role with no PIN set)
  //  locked   — overlay shown, PIN required to unlock
  //  unlocked — normal app use
  const [stage, setStage]   = useState(PIN_LOCK_ENABLED ? 'loading' : 'unlocked');
  const [statusErr, setErr] = useState(null);
  const bcRef       = useRef(null);
  const timerRef    = useRef(null);
  const hiddenSince = useRef(null);
  // Mirror of `stage` for handlers that must NOT re-subscribe on every change
  // (the BroadcastChannel listener + the inactivity timer read this).
  const stageRef    = useRef(stage);
  stageRef.current  = stage;
  // Live mirrors of the per-role timings so the running timers pick up a change
  // (e.g. an admin republishes roleDefinitions mid-session) without the effects
  // re-subscribing on every render — the next tick just reads the new value.
  const inactivityMsRef  = useRef(inactivityMs);
  inactivityMsRef.current  = inactivityMs;
  const hiddenGraceMsRef = useRef(hiddenGraceMs);
  hiddenGraceMsRef.current = hiddenGraceMs;

  // Resolve the right stage from a /pin/status response. Used by the initial
  // fetch and by recheckStatus() after server-side changes (admin reset, etc.).
  // Decides auto-lock from the response's own preference (resolveLock) rather
  // than the render-time `autoLock`, which lags a tick behind a freshly fetched
  // preference — otherwise a SuperAdmin who has opted into auto-lock would flash
  // unlocked before the state settled.
  const applyStatus = useCallback((self) => {
    const nextPref = self.lockInactivityMs ?? null;
    setLockPrefMs(nextPref);
    const { autoLock: al } = resolveLock({
      required, isSuperAdmin, roleExempt, roleInactivityMs, roleHiddenGraceMs, prefMs: nextPref,
    });
    if (self.required && !self.isSet) setStage('setup');
    // A role that never auto-locks starts unlocked even with a PIN set — it only
    // enters the locked state on a manual "Lock now".
    else if (self.isSet)              setStage(prev => prev === 'unlocked' ? 'unlocked' : (al ? 'locked' : 'unlocked'));
    else                              setStage('unlocked');
  }, [required, isSuperAdmin, roleExempt, roleInactivityMs, roleHiddenGraceMs]);

  const recheckStatus = useCallback(async () => {
    try {
      const { self } = await getPinStatus();
      applyStatus(self);
    } catch (e) {
      console.warn('[PinLock] recheck failed:', e.message);
    }
  }, [applyStatus]);

  // ── Initial PIN status fetch ─────────────────────────────────────────────────
  useEffect(() => {
    if (!PIN_LOCK_ENABLED) {
      setBootStage('pin.skip', 'Session PIN disabled');
      return;
    }
    if (!email) return;
    let cancelled = false;
    setBootStage('pin.status', 'Checking session PIN', email);
    (async () => {
      try {
        const { self } = await getPinStatus();
        if (cancelled) return;
        // First load: if a PIN is set, force a lock on entry — unless the
        // resolved behaviour is manual-only (SuperAdmin without an opt-in
        // window, an exempt role, or "Never"). applyStatus also stores the
        // user's fetched duration preference.
        applyStatus(self);
        setBootStage('pin.ready', 'Session PIN ready', self.isSet ? 'PIN set' : 'No PIN set');
      } catch (e) {
        if (cancelled) return;
        // Fail-open: a broken PIN endpoint shouldn't lock everyone out of the
        // whole app. Required roles still get blocked at setup if they later
        // try to use sensitive endpoints.
        console.warn('[PinLock] status fetch failed — running unlocked:', e.message);
        setBootStage('pin.status', 'PIN check failed — continuing unlocked', e.message);
        setErr(e.message);
        setStage('unlocked');
      }
    })();
    return () => { cancelled = true; };
  }, [email, applyStatus]);

  // Log one session_start per browser tab when the user reaches the unlocked app.
  useEffect(() => {
    if (stage !== 'unlocked' || !email) return;
    try {
      if (sessionStorage.getItem(SESSION_LOG_KEY)) return;
      sessionStorage.setItem(SESSION_LOG_KEY, '1');
      recordAccessSession().catch(() => {});
    } catch { /* sessionStorage unavailable */ }
  }, [stage, email]);

  // ── Cross-tab broadcast (lock all tabs at once) ──────────────────────────────
  // Subscribe ONCE (mount-only). Reading stageRef instead of `stage` avoids
  // tearing down and recreating the channel on every transition — the old
  // [stage] dependency dropped messages during the re-subscribe gap.
  useEffect(() => {
    if (!PIN_LOCK_ENABLED || typeof BroadcastChannel === 'undefined') return;
    const bc = new BroadcastChannel(BROADCAST_CHANNEL);
    bcRef.current = bc;
    bc.onmessage = (ev) => {
      const next = applyBroadcastTransition(stageRef.current, ev.data?.type);
      if (!next) return;
      if (next === 'unlocked') {
        // Another tab unlocked → the user is active. Refresh BOTH stamps and drop
        // any stale hiddenSince so neither the inactivity guard nor the visibility
        // guard can immediately re-lock and bounce every tab back.
        markActive(true);
        hiddenSince.current = null;
      } else {
        hiddenSince.current = null;
      }
      setStage(next);
    };
    return () => { bc.close(); bcRef.current = null; };
  }, []);

  const broadcast = (type) => { try { bcRef.current?.postMessage({ type }); } catch {} };

  const lock = useCallback(() => {
    if (!PIN_LOCK_ENABLED) return;
    hiddenSince.current = null;
    setStage(prev => (prev === 'unlocked' ? 'locked' : prev));
    broadcast('lock');
  }, []);

  // ── Inactivity timer ─────────────────────────────────────────────────────────
  // Stamps activity (per-tab + shared) and schedules a check. When the timer
  // fires it only locks if the user has been inactive EVERYWHERE — a background
  // tab whose own timer fires must not lock when another tab saw recent activity
  // (otherwise an idle background tab snaps every tab back to the lock screen).
  const resetTimer = useCallback(() => {
    if (stageRef.current !== 'unlocked') return;
    markActive();
    if (timerRef.current) clearTimeout(timerRef.current);
    const tick = () => {
      if (stageRef.current !== 'unlocked') return;
      if (isInactive(Date.now(), readPerTab(), readShared(), inactivityMsRef.current)) lock();
      else timerRef.current = setTimeout(tick, inactivityMsRef.current); // active elsewhere → recheck later
    };
    timerRef.current = setTimeout(tick, inactivityMsRef.current);
  }, [lock]);

  useEffect(() => {
    // SuperAdmin is auto-lock-exempt: no inactivity timer at all.
    if (!autoLock || stage !== 'unlocked') {
      if (timerRef.current) clearTimeout(timerRef.current);
      return;
    }

    // If the session restored mid-window and the user has been inactive across
    // ALL tabs, lock immediately — don't grant a fresh window for free. Consults
    // the shared stamp so a tab unlocked via broadcast doesn't re-lock itself.
    if (isInactive(Date.now(), readPerTab(), readShared(), inactivityMsRef.current)) {
      lock();
      return;
    }

    resetTimer();
    const handler = () => resetTimer();
    ACTIVITY_EVENTS.forEach(ev => window.addEventListener(ev, handler, { passive: true }));
    return () => {
      ACTIVITY_EVENTS.forEach(ev => window.removeEventListener(ev, handler));
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [stage, resetTimer, lock, autoLock]);

  // ── Tab visibility (hidden > 30s → lock) ─────────────────────────────────────
  useEffect(() => {
    // Auto-lock-exempt roles (SuperAdmin) don't lock on tab-hide either.
    if (!autoLock || stage !== 'unlocked') return;
    function onVis() {
      if (document.hidden) {
        hiddenSince.current = Date.now();
      } else if (hiddenSince.current) {
        const h = hiddenSince.current;
        hiddenSince.current = null;
        // Don't re-lock if the user was active in another tab while this one was
        // hidden (shared stamp newer than when we went hidden).
        if (shouldLockOnVisibilityRestore(h, Date.now(), readShared(), hiddenGraceMsRef.current)) lock();
      }
    }
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [stage, lock, autoLock]);

  // ── Actions exposed to the overlay / user menu ───────────────────────────────
  const submitUnlock = useCallback(async (pin) => {
    try {
      await verifyPin(pin);
      markActive(true);
      hiddenSince.current = null;
      setStage('unlocked');
      broadcast('unlock');
    } catch (e) {
      // Admin reset (or any other clearing of the PIN) leaves the user staring
      // at a "Session locked" screen with no way forward. If the server says
      // there's no PIN set, recheck status so the overlay flips to setup mode.
      if (/no pin is set/i.test(e.message || '')) {
        await recheckStatus();
      }
      throw e;
    }
  }, [recheckStatus]);

  const submitSetup = useCallback(async (pin, currentPin) => {
    await setPin(pin, currentPin);
    markActive(true);
    hiddenSince.current = null;
    setStage('unlocked');
    broadcast('unlock');
  }, []);

  // Change the caller's own auto-lock duration (Supervisor + SuperAdmin).
  //   ms > 0 → inactivity window; ms === 0 → "Never" (SuperAdmin); null → clear.
  // Optimistic: the running timers pick up the new window immediately via the
  // refs above; on failure we roll the selection back.
  const setLockDuration = useCallback(async (ms) => {
    const prev = lockPrefMs;
    setLockPrefMs(ms);
    // If the change turns auto-lock ON, treat it as fresh activity so the timer
    // doesn't fire against a stale stamp and lock the user out immediately.
    markActive(true);
    try {
      await setLockPref(ms);
    } catch (e) {
      setLockPrefMs(prev);
      throw e;
    }
  }, [lockPrefMs]);

  const value = useMemo(() => ({
    enabled: PIN_LOCK_ENABLED,
    stage,
    required,
    statusErr,
    lock,
    submitUnlock,
    submitSetup,
    recheckStatus,
    // Auto-lock duration user setting.
    canSetLockDuration,
    canDisableAutoLock: isSuperAdmin,  // only SuperAdmin may choose "Never"
    lockPrefMs: prefMs,                // null | 0 | >0
    roleDefaultInactivityMs: roleInactivityMs,
    effectiveInactivityMs: inactivityMs,
    autoLock,
    setLockDuration,
  }), [
    stage, required, statusErr, lock, submitUnlock, submitSetup, recheckStatus,
    canSetLockDuration, isSuperAdmin, prefMs, roleInactivityMs, inactivityMs, autoLock, setLockDuration,
  ]);

  return (
    <PinLockContext.Provider value={value}>
      {children}
    </PinLockContext.Provider>
  );
}
